import { ErrorService } from '@/core/shared/errors/error.service';
import { JobPersistence } from '@/core/shared/job/job.persistence';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import TrackingService from '@/core/shared/tracking/tracking.service';

import { emptySkillSuggestionsResult, useSkillSuggestionsStore } from './skill-suggestions.store';
import type {
  SkillSuggestionDisplay,
  SkillSuggestionFallbackReason,
  SkillSuggestionField,
  SkillSuggestionSource,
  SkillSuggestionsResult,
  SkillSuggestionsState,
} from './skill-suggestions.type';
import {
  buildContentFingerprint,
  cleanSuggestionResult,
  hasUsableContent,
} from './skill-suggestions.utils';

export const SKILL_SUGGESTIONS_POLL_INTERVAL_MS = 750;
export const SKILL_SUGGESTIONS_GENERATE_TIMEOUT_MS = 15000;
export const SKILL_SUGGESTIONS_POLL_BUDGET_MS = 30000;

export type SkillSuggestionsPhase = 'taxonomy' | 'loading' | 'ai';

class SkillSuggestionsTimeoutError extends Error {}

export default class SkillSuggestionsService {
  private readonly store = useSkillSuggestionsStore();
  private readonly jobPersistence = new JobPersistence();
  private disposed = false;

  public get state(): SkillSuggestionsState {
    return this.store.state;
  }

  public get result(): SkillSuggestionsResult {
    return this.store.result;
  }

  // What the form should show for the job content it currently holds. Loading is only reported
  // while a run is in flight for that exact content; any other leftover state is not ours to
  // wait on, so the form shows today's taxonomy suggestions.
  public phaseFor(title: string | null, description: string | null): SkillSuggestionsPhase {
    if (this.store.fingerprint !== buildContentFingerprint(title, description)) {
      return 'taxonomy';
    }
    if (this.store.state === 'loading') {
      return 'loading';
    }
    return this.store.state === 'ready' ? 'ai' : 'taxonomy';
  }

  public async request(title: string | null, description: string | null): Promise<void> {
    if (this.disposed) {
      return;
    }
    const fingerprint = buildContentFingerprint(title, description);
    if (this.store.fingerprint === fingerprint) {
      return;
    }

    const runId = this.startRun(fingerprint);

    if (!hasUsableContent(title, description)) {
      this.store.state = 'idle';
      return;
    }

    this.store.state = 'loading';
    await this.run(runId, {
      jobTitle: (title ?? '').trim(),
      jobDescription: description ?? '',
    });
  }

  public dispose(): void {
    this.disposed = true;
    this.store.reset();
  }

  public trackShown(field: SkillSuggestionField, suggestions: SkillSuggestionDisplay[]): void {
    if (!suggestions.length) {
      return;
    }
    this.trackSilently(TrackingActionName.SKILL_SUGGESTIONS_SHOWN, {
      field,
      source: 'ai',
      count: suggestions.length,
      canonicalCount: suggestions.filter((s) => s.kind === 'canonical').length,
      customCount: suggestions.filter((s) => s.kind === 'custom').length,
      suggestions: suggestions.map((s) => s.text),
    });
  }

  public trackAccepted(
    field: SkillSuggestionField,
    source: SkillSuggestionSource,
    suggestion: SkillSuggestionDisplay,
  ): void {
    this.trackSilently(TrackingActionName.SKILL_SUGGESTION_ACCEPTED, {
      field,
      source,
      classification: suggestion.kind,
      suggestion: suggestion.text,
    });
  }

  private startRun(fingerprint: string): number {
    this.store.cancelWait?.();
    this.store.cancelWait = null;
    this.store.activeRunId += 1;
    this.store.fingerprint = fingerprint;
    this.store.result = emptySkillSuggestionsResult();
    return this.store.activeRunId;
  }

  private isActive(runId: number): boolean {
    return this.store.activeRunId === runId;
  }

  private async run(
    runId: number,
    input: { jobTitle: string; jobDescription: string },
  ): Promise<void> {
    const startedAt = Date.now();
    let reported = false;

    try {
      const documentId = await this.withDeadline(
        this.jobPersistence.generateSkillSuggestions(input),
        startedAt + SKILL_SUGGESTIONS_GENERATE_TIMEOUT_MS,
      );
      // The polling budget starts once the generate POST has returned, so a slow cold start there
      // does not eat into the time the result needs.
      const deadline = Date.now() + SKILL_SUGGESTIONS_POLL_BUDGET_MS;

      while (this.isActive(runId)) {
        await this.sleep(SKILL_SUGGESTIONS_POLL_INTERVAL_MS);
        if (!this.isActive(runId)) {
          return;
        }

        let skillSuggestions;
        try {
          ({ skillSuggestions } = await this.withDeadline(
            this.jobPersistence.getGeneratedSkillSuggestions(documentId),
            deadline,
          ));
        } catch (error) {
          if (error instanceof SkillSuggestionsTimeoutError || !this.isActive(runId)) {
            throw error;
          }
          // A transient poll failure is retried until the budget runs out.
          if (!reported) {
            reported = true;
            ErrorService.captureException(error);
          }
          if (Date.now() >= deadline) {
            throw new SkillSuggestionsTimeoutError();
          }
          continue;
        }
        if (!this.isActive(runId)) {
          return;
        }

        if (skillSuggestions.status === 'fulfilled') {
          this.resolveFulfilled(skillSuggestions, startedAt);
          return;
        }
        if (skillSuggestions.status === 'failed') {
          this.resolveFallback('failed', startedAt);
          return;
        }
        if (Date.now() >= deadline) {
          throw new SkillSuggestionsTimeoutError();
        }
      }
    } catch (error) {
      if (!this.isActive(runId)) {
        return;
      }
      if (error instanceof SkillSuggestionsTimeoutError) {
        this.resolveFallback('timeout', startedAt);
        return;
      }
      if (!reported) {
        ErrorService.captureException(error);
      }
      this.resolveFallback('error', startedAt);
    }
  }

  private resolveFulfilled(
    suggestions: Partial<Record<SkillSuggestionField, unknown>>,
    startedAt: number,
  ): void {
    const result = cleanSuggestionResult(suggestions);
    if (!result.knowledgeDisciplines.length && !result.technologiesMachines.length) {
      this.resolveFallback('empty', startedAt);
      return;
    }

    this.store.result = result;
    this.store.state = 'ready';
    this.trackSilently(TrackingActionName.SKILL_SUGGESTIONS_RESOLVED, {
      outcome: 'ai',
      durationMs: Date.now() - startedAt,
      knowledgeDisciplinesCount: result.knowledgeDisciplines.length,
      technologiesMachinesCount: result.technologiesMachines.length,
    });
  }

  private resolveFallback(reason: SkillSuggestionFallbackReason, startedAt: number): void {
    this.store.result = emptySkillSuggestionsResult();
    // Cleared so the next natural trigger can try again; nothing retries on its own.
    this.store.fingerprint = null;
    this.store.state = 'fallback';
    this.trackSilently(TrackingActionName.SKILL_SUGGESTIONS_RESOLVED, {
      outcome: 'fallback',
      reason,
      durationMs: Date.now() - startedAt,
    });
  }

  private withDeadline<T>(promise: Promise<T>, deadline: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new SkillSuggestionsTimeoutError()),
        Math.max(deadline - Date.now(), 0),
      );
      promise.then(resolve, reject).finally(() => clearTimeout(timer));
    });
  }

  private sleep(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        this.store.cancelWait = null;
        resolve();
      }, ms);
      this.store.cancelWait = () => {
        clearTimeout(timer);
        resolve();
      };
    });
  }

  private trackSilently(action: TrackingActionName, properties: Record<string, unknown>): void {
    TrackingService.trackAction(action, properties).catch(() => undefined);
  }
}
